// agents/src/components/BlockContactModal.jsx
import { useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Modal para confirmar el bloqueo de un contacto
 * @param {boolean} isOpen - Estado del modal (abierto/cerrado)
 * @param {function} onClose - Función para cerrar el modal
 * @param {function} onBlock - Función callback al bloquear (recibe reason)
 * @param {string} whatsappId - Número de WhatsApp del contacto
 */
export default function BlockContactModal({ isOpen, onClose, onBlock, whatsappId }) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!reason.trim()) {
      setError('Debes proporcionar una razón para el bloqueo');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      await onBlock(reason.trim());
      onClose();
      setReason(''); // Limpiar para la próxima vez
    } catch (err) {
      setError(err.message || 'Error al bloquear contacto');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setReason('');
      setError('');
      onClose();
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div style={styles.overlay} onClick={handleClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h2 style={styles.title}>⚠️ Bloquear Contacto</h2>
          <button style={styles.closeBtn} onClick={handleClose} disabled={submitting}>
            ×
          </button>
        </div>

        <div style={styles.body}>
          <div style={styles.warningBox}>
            <p style={styles.warningText}>
              <strong>¿Estás seguro de bloquear este contacto?</strong>
            </p>
            <p style={styles.contactInfo}>
              WhatsApp: <strong>{whatsappId}</strong>
            </p>
            <p style={styles.warningSubtext}>
              Este contacto no podrá crear nuevas conversaciones ni enviar mensajes.
            </p>
          </div>

          {error && (
            <div style={styles.errorBox}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={styles.formGroup}>
              <label style={styles.label}>
                Razón del bloqueo: <span style={styles.required}>*</span>
              </label>
              <textarea
                style={styles.textarea}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Explica por qué estás bloqueando este contacto..."
                rows={4}
                disabled={submitting}
                required
              />
              <small style={styles.helpText}>
                Esta información quedará registrada y será visible para otros administradores.
              </small>
            </div>

            <div style={styles.footer}>
              <button
                type="button"
                style={styles.cancelBtn}
                onClick={handleClose}
                disabled={submitting}
              >
                Cancelar
              </button>
              <button
                type="submit"
                style={{...styles.blockBtn, opacity: submitting ? 0.6 : 1}}
                disabled={submitting}
              >
                {submitting ? 'Bloqueando...' : '🚫 Bloquear Contacto'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>,
    document.body
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    width: '90%',
    maxWidth: '550px',
    maxHeight: '85vh',
    overflow: 'auto',
    boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1.5rem',
    borderBottom: '1px solid #e0e0e0'
  },
  title: {
    margin: 0,
    fontSize: '1.5rem',
    color: '#d32f2f'
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '2rem',
    color: '#666',
    cursor: 'pointer',
    padding: '0',
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '4px',
    transition: 'background-color 0.2s'
  },
  body: {
    padding: '1.5rem'
  },
  warningBox: {
    backgroundColor: '#fff3e0',
    border: '2px solid #ff9800',
    borderRadius: '8px',
    padding: '1rem',
    marginBottom: '1.5rem'
  },
  warningText: {
    margin: '0 0 0.5rem 0',
    color: '#e65100',
    fontSize: '1rem'
  },
  contactInfo: {
    margin: '0.5rem 0',
    fontSize: '0.95rem',
    color: '#333'
  },
  warningSubtext: {
    margin: '0.75rem 0 0 0',
    fontSize: '0.9rem',
    color: '#666'
  },
  errorBox: {
    margin: '0 0 1rem 0',
    padding: '0.75rem',
    backgroundColor: '#fee',
    border: '1px solid #fcc',
    borderRadius: '6px',
    color: '#c00',
    fontSize: '0.9rem'
  },
  formGroup: {
    marginBottom: '1.5rem'
  },
  label: {
    display: 'block',
    marginBottom: '0.5rem',
    fontWeight: '600',
    color: '#333',
    fontSize: '0.95rem'
  },
  required: {
    color: '#d32f2f'
  },
  textarea: {
    width: '100%',
    padding: '0.75rem',
    border: '2px solid #ddd',
    borderRadius: '8px',
    fontSize: '0.95rem',
    fontFamily: 'inherit',
    resize: 'vertical',
    minHeight: '100px',
    outline: 'none',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box'
  },
  helpText: {
    display: 'block',
    marginTop: '0.5rem',
    fontSize: '0.85rem',
    color: '#666',
    fontStyle: 'italic'
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.75rem',
    paddingTop: '1rem',
    borderTop: '1px solid #e0e0e0'
  },
  cancelBtn: {
    padding: '0.625rem 1.25rem',
    border: '2px solid #ddd',
    backgroundColor: '#fff',
    borderRadius: '8px',
    fontSize: '0.95rem',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s',
    color: '#666'
  },
  blockBtn: {
    padding: '0.625rem 1.25rem',
    border: 'none',
    backgroundColor: '#d32f2f',
    color: '#fff',
    borderRadius: '8px',
    fontSize: '0.95rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s'
  }
};
